import './applyPurchaseTypePage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { PurchaseType } from '@thesavingsgroup/enums/PurchaseType';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const { purchaseType, setPurchaseType } = useApplyStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      purchaseTypeValue: purchaseType,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<PurchaseType>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const purchaseTypeValue = event.target.value;

    setIsInitialSelection(isEmpty(purchaseType) && isValidClickEvent(event));

    setPurchaseType(purchaseTypeValue);
  };

  const formSubmit = (values: FieldValues): void => {
    const { purchaseTypeValue } = values;

    if (purchaseTypeValue) {
      navigate(next());
    }
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <div className="apply-purchase-type-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header
            progress={useWorkflowStore.getState().computed.progress}
            showDrawerToggle
            onBackClick={previousStep}
          />

          <main>
            <form
              data-testid={locator.pages.applyPurchaseType.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applyPurchaseType.form.h1}>
                  {t('ApplyPurchaseTypePage.title')}
                </h1>
                <h2 data-testid={locator.pages.applyPurchaseType.form.h2}>
                  {t('ApplyPurchaseTypePage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <RadioGroup
                    labelPlacement="below"
                    name="purchaseTypeValue"
                    options={[
                      {
                        label: t('ApplyPurchaseTypePage.dealer'),
                        value: PurchaseType.DEALER,
                        testId:
                          locator.pages.applyPurchaseType.form.radioOption1,
                      },
                      {
                        label: t('ApplyPurchaseTypePage.privateParty'),
                        value: PurchaseType.PRIVATE_PARTY,
                        testId:
                          locator.pages.applyPurchaseType.form.radioOption2,
                      },
                    ]}
                    register={register}
                    testId={locator.pages.applyPurchaseType.form.radioGroupRoot}
                    variant="inline"
                    watch={watch}
                    onClick={radioOptionClicked}
                  />
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <Button
                    data-testid={
                      locator.pages.applyPurchaseType.form.submitButton
                    }
                    disabled={isEmpty(purchaseType)}
                    fullWidth
                    submit
                  >
                    {t('ApplyPurchaseTypePage.continue')}
                  </Button>
                </div>
              </div>
              <footer data-testid={locator.pages.applyPurchaseType.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplyPurchaseTypePage';
